/* Card quick add: drives the quick view / add-to-cart layout on product
   cards (product-card.liquid, show_quickadd contexts). The permanent +
   toggle opens the two action buttons on click — same flow on all devices
   (mobile just renders them icon-only via CSS).

   Modes (data-qa-mode on the ATC pill, decided in Liquid):
     instant - one purchasable variant, adds straight to cart
     sizes   - one option with several values, opens the inline chip strip
     modal   - two+ options, falls back to the quick view modal

   Adds go through the Cart AJAX API with the cart drawer + header bubble
   sections requested, and both are re-rendered silently — the drawer stays
   closed (feedback is the pill's "Added" state and the bubble bump). Mirrors
   product-form.js's event contract (variant:add, cart:change, cartUpdate) so
   the drawer widgets stay in sync. */

if (!customElements.get('card-quick-add')) {
	customElements.define(
		'card-quick-add',
		class CardQuickAdd extends HTMLElement {
			constructor() {
				super()

				this.atcButton = this.querySelector('.card__qa-pill--atc')
				this.qvButton = this.querySelector('.card__qa-pill--qv')
				this.toggleButton = this.querySelector('.card__qa-toggle')
				this.sizesStrip = this.querySelector('.card__qa-sizes')
				this.busy = false

				if (this.toggleButton) {
					this.toggleButton.addEventListener('click', this.onToggle.bind(this))
				}
				if (this.atcButton) {
					this.atcButton.addEventListener('click', this.onAtcClick.bind(this))
				}
				if (this.sizesStrip) {
					this.sizesStrip
						.querySelectorAll('.card__qa-size')
						.forEach((chip) =>
							chip.addEventListener('click', () =>
								this.addToCart(chip.dataset.variantId, chip)
							)
						)
				}

				// While the pointer is on the card the widget stays open — no timer
				// may close it out from under someone who is still using it. Leaving
				// starts a grace period (a brief drift shouldn't dismiss it), after
				// which the normal close rules resume.
				const card = this.closest('.product-card')
				if (card) {
					// Hover devices only: on touch these fire on taps and would fight
					// the outside-tap closer below
					const hoverCapable = () =>
						window.matchMedia('(hover: hover)').matches

					card.addEventListener('mouseenter', () => {
						if (!hoverCapable()) return
						this.pointerInside = true
						clearTimeout(this.leaveTimer)
					})
					card.addEventListener('mouseleave', () => {
						if (!hoverCapable()) return
						this.pointerInside = false
						clearTimeout(this.leaveTimer)
						this.leaveTimer = setTimeout(() => this.closeIfIdle(), 600)
					})
				}

				this.addEventListener('keyup', (evt) => {
					if (evt.code !== 'Escape') return
					const sizesWereOpen = this.classList.contains('sizes-open')
					this.closeAll()
					if (sizesWereOpen && this.atcButton) this.atcButton.focus()
				})
			}

			onToggle() {
				const open = this.classList.toggle('is-open')
				this.toggleButton.setAttribute('aria-expanded', open)
				if (open) {
					this.holdSlider()
				} else {
					this.closeSizes()
					this.releaseSlider()
				}
			}

			/* Autoplay sliders (popular-products, popular-products-v2, product
			   recommendations) would keep advancing under an open widget, yanking
			   the card away mid-interaction. Hold the parent Swiper's autoplay
			   while anything is open and resume once fully closed. */
			get swiper() {
				if (this._swiperEl === undefined) {
					const el = this.closest('.swiper')
					this._swiperEl = el || null
				}
				return this._swiperEl && this._swiperEl.swiper
					? this._swiperEl.swiper
					: null
			}

			holdSlider() {
				const swiper = this.swiper
				if (swiper && swiper.autoplay && swiper.autoplay.running) {
					this.sliderHeld = true
					swiper.autoplay.stop()
				}
			}

			releaseSlider() {
				// Only resume what we paused, and only once everything is closed
				if (!this.sliderHeld) return
				if (
					this.busy ||
					this.classList.contains('is-open') ||
					this.classList.contains('sizes-open') ||
					this.classList.contains('is-added')
				) {
					return
				}
				this.sliderHeld = false
				const swiper = this.swiper
				if (swiper && swiper.autoplay) swiper.autoplay.start()
			}

			onAtcClick(evt) {
				if (this.busy) return
				const mode = this.atcButton.dataset.qaMode

				if (mode === 'instant') {
					this.addToCart(this.atcButton.dataset.variantId, this.atcButton)
					return
				}
				if (mode === 'modal') {
					// Can't pick two option dimensions inline - hand over to quick view
					if (this.qvButton) this.qvButton.click()
					return
				}

				this.classList.add('sizes-open')
				this.atcButton.setAttribute('aria-expanded', 'true')
				this.holdSlider()
				// Keyboard activation (click with no pointer coords): move focus into
				// the strip, since the pills just visually yielded to it
				if (evt.detail === 0 && this.sizesStrip) {
					const first = this.sizesStrip.querySelector(
						'.card__qa-size:not([disabled])'
					)
					if (first) first.focus()
				}
			}

			closeSizes() {
				this.classList.remove('sizes-open')
				if (this.atcButton && this.atcButton.dataset.qaMode === 'sizes') {
					this.atcButton.setAttribute('aria-expanded', 'false')
				}
			}

			closeAll() {
				clearTimeout(this.leaveTimer)
				this.closeSizes()
				this.classList.remove('is-open')
				if (this.toggleButton) {
					this.toggleButton.setAttribute('aria-expanded', 'false')
				}
				this.releaseSlider()
			}

			/* Active use — no timer may close the widget during any of these.
			   Hovering counts, so does keyboard focus inside it and an add still
			   in flight. */
			isEngaged() {
				if (this.busy) return true
				if (this.pointerInside) return true
				return this.contains(document.activeElement)
			}

			/* The single exit used by every timer: close only once the shopper has
			   actually left. Transient feedback states own their own timers, so
			   they finish the close themselves rather than being cut short here. */
			closeIfIdle() {
				if (this.isEngaged()) return
				if (
					this.classList.contains('is-added') ||
					this.classList.contains('has-error')
				) {
					return
				}
				this.closeAll()
			}

			setLoading(sourceButton, on) {
				// .is-busy dims the sibling chips while one is adding
				this.classList.toggle('is-busy', on)
				if (sourceButton && sourceButton.classList.contains('card__qa-size')) {
					sourceButton.classList.toggle('loading', on)
				}
				if (this.atcButton) {
					this.atcButton.classList.toggle('loading', on)
					const spinner = this.atcButton.querySelector(
						'.loading-overlay__spinner'
					)
					if (spinner) spinner.classList.toggle('hidden', !on)
				}
			}

			addToCart(variantId, sourceButton) {
				if (this.busy || !variantId) return
				this.busy = true
				clearTimeout(this.leaveTimer)
				this.holdSlider()
				this.setLoading(sourceButton, true)

				const cart =
					document.querySelector('cart-drawer') ||
					document.querySelector('cart-notification')

				const config = fetchConfig('javascript')
				config.headers['X-Requested-With'] = 'XMLHttpRequest'
				delete config.headers['Content-Type']

				const formData = new FormData()
				formData.append('id', variantId)
				formData.append('quantity', 1)
				if (cart && typeof cart.getSectionsToRender === 'function') {
					formData.append(
						'sections',
						cart.getSectionsToRender().map((section) => section.id)
					)
					formData.append('sections_url', window.location.pathname)
				}
				config.body = formData

				fetch(`${routes.cart_add_url}`, config)
					.then((response) => response.json())
					.then((response) => {
						if (response.status) {
							// Variant went unavailable between render and click
							document.dispatchEvent(
								new CustomEvent('cart:error', {
									detail: {
										source: 'card-quick-add',
										productVariantId: variantId,
										errors: response.description,
										message: response.message,
									},
								})
							)
							if (window.publish && window.PUB_SUB_EVENTS) {
								publish(PUB_SUB_EVENTS.cartError, {
									source: 'card-quick-add',
									productVariantId: variantId,
									errors: response.description,
									message: response.message,
								})
							}
							this.showError(sourceButton)
							return
						}

						this.renderCartSections(cart, response)

						document.dispatchEvent(
							new CustomEvent('variant:add', {
								detail: {
									variant: { id: variantId },
									quantity: 1,
									formElement: null,
									sectionId: this.dataset.sectionId,
								},
							})
						)
						fetch(`${routes.cart_url}.js`)
							.then((res) => res.text())
							.then((state) => {
								document.dispatchEvent(
									new CustomEvent('cart:change', {
										detail: {
											cart: JSON.parse(state),
											sectionId: this.dataset.sectionId,
										},
									})
								)
							})
							.catch((error) => {
								console.error('Error fetching cart state:', error)
							})
						if (window.publish && window.PUB_SUB_EVENTS) {
							publish(PUB_SUB_EVENTS.cartUpdate, {
								source: 'card-quick-add',
								productVariantId: variantId,
							})
						}

						this.showAdded()
					})
					.catch((e) => {
						console.error(e)
						this.showError(sourceButton)
					})
					.finally(() => {
						this.busy = false
						this.setLoading(sourceButton, false)
					})
			}

			renderCartSections(cart, response) {
				if (cart && typeof cart.getSectionsToRender === 'function' && response.sections) {
					cart.getSectionsToRender().forEach((section) => {
						const element = section.selector
							? document.querySelector(section.selector)
							: document.getElementById(section.id)
						const html = response.sections[section.id]
						if (!element || !html) return
						element.innerHTML = new DOMParser()
							.parseFromString(html, 'text/html')
							.querySelector(section.selector || '.shopify-section').innerHTML
					})
					cart.classList.remove('is-empty')
					const drawerInner = cart.querySelector('.drawer__inner')
					if (drawerInner) drawerInner.classList.remove('is-empty')
					// Replacing #CartDrawer's innerHTML dropped the overlay's close
					// listener (renderContents re-binds it the same way)
					const overlay = cart.querySelector('#CartDrawer-Overlay')
					if (overlay && typeof cart.close === 'function') {
						overlay.addEventListener('click', cart.close.bind(cart))
					}
				}

				const bubble =
					document.querySelector('#cart-icon-bubble .cart-count-bubble') ||
					document.getElementById('cart-icon-bubble')
				if (bubble) {
					bubble.classList.remove('cart-count-bubble--bump')
					void bubble.offsetWidth
					bubble.classList.add('cart-count-bubble--bump')
				}
			}

			/* The confirmation is the only feedback the shopper gets (the drawer
			   stays shut), so it holds for a full beat before anything closes —
			   and nothing else is allowed to close the widget while it shows.
			   Once it has been read, the widget only collapses if the pointer has
			   left; a shopper still on the card keeps it open to add another. */
			showAdded() {
				this.closeSizes()
				this.classList.remove('has-error')
				this.classList.add('is-added')
				clearTimeout(this.addedTimer)
				this.addedTimer = setTimeout(() => {
					this.classList.remove('is-added')
					this.closeIfIdle()
					this.releaseSlider()
				}, 2600)
			}

			showError(sourceButton) {
				if (sourceButton && sourceButton.classList.contains('card__qa-size')) {
					sourceButton.disabled = true
				}
				this.closeSizes()
				this.classList.remove('is-added')
				this.classList.add('has-error')
				clearTimeout(this.errorTimer)
				this.errorTimer = setTimeout(() => {
					this.classList.remove('has-error')
					this.closeIfIdle()
					this.releaseSlider()
				}, 2600)
			}
		}
	)

	// One shared outside-click closer: any open widget collapses when the tap
	// lands outside it — except mid-add or while the confirmation is showing
	document.addEventListener('click', (evt) => {
		document
			.querySelectorAll('card-quick-add.is-open, card-quick-add.sizes-open')
			.forEach((el) => {
				if (el.contains(evt.target)) return
				if (el.busy || el.classList.contains('is-added')) return
				el.closeAll()
			})
	})
}
